// Types for SILAE form
export type SourceType =
  | "fixed" // Pourcentage fixe (ex: 5%)
  | "evp" // Variable EVP
  | "salary_base" // Salaire de base
  | "salary_gross" // Salaire brut
  | "salary_net" // Salaire net
  | "stored_variable" // Variable stockée
  | "cumul_bulletins" // Cumul bulletins
  | "cumul_stock_var" // Cumul variable stockée
  | "advanced_variable" // Variable SILAE avancée
  | "complex_expression" // Expression complexe
  | "questionnaire_variable" // Variable questionnaire salarié
  | "manual_formula"; // Formule manuelle

export type SilaeConditionType =
  | "VAR_SALARY_BASE"
  | "VAR_SALARY_GROSS"
  | "VAR_SALARY_NET"
  | "VAR_CONTRACT_TYPE"
  | "VAR_PROFESSIONAL_STATUS"
  | "VAR_EMPLOYEE_CATEGORY"
  | "VAR_AGE"
  | "VAR_SENIORITY"
  | "VAR_MONTH"
  | "VAR_YEAR_CURRENT"
  | "VAR_IS_EXECUTIVE"
  | "silae_variable"
  | "cumulstockvar"
  | "cumul_bulletins"
  | "questionnaire_variable"
  | "custom";

export type SilaeConditionOperator = "=" | ">=" | "<=" | "<>" | ">" | "<";

export type StructureType = "simple" | "with_conditions" | "if_else";

export type CalculMethod = "1" | "2" | "3" | "99";

export type CounterAndStockageValueType =
  | "bases"
  | "tauxs"
  | "bases_times_tauxs"
  | "custom";

export type CounterType =
  | "AjouteCpAcquisRef"       // CP acquis année N
  | "AjouteCPAcquisAnt"       // CP acquis année N-1
  | "AjouteCPPrisRef"         // CP pris année N
  | "AjouteCPPrisAnt"         // CP pris année N-1
  | "AffecteCPSupJoursAcquis" // CP Sup 1 acquis
  | "AffecteCPSupJoursPris"   // CP Sup 1 pris
  | "AffecteCPSup2JoursAcquis"// CP Sup 2 acquis
  | "AffecteCPSup2JoursPris"  // CP Sup 2 pris
  | "AjouteRTTAcquis"         // RTT acquis
  | "AjouteRTTpris";          // RTT pris
export interface AdvancedVariableParams {
  id: string;
  isRequired: boolean; // Indique si le paramètre est obligatoire au cours de la configuration
  label?: string; // Libellé du paramètre
  type?: string; // Type de paramètre (ex: "date", "year", "int", "string", "double", "boolean", "selection", "function", "variable")
  includeBulletinCourant?: boolean; // Indique si le paramètre présente le switch pour inclure les bulletins courants (ajouter 10 à l'indice alors les bulletins courant seront pris en compte)
  shouldIncludeBulletinCourant?: boolean; // Indique si le paramètre doit inclure les bulletins courants (ajouter 10 à l'indice alors les bulletins courant seront pris en compte)
  note?: string; // Note pour l'utilisateur
  placeholder?: string; // Placeholder pour l'utilisateur
  helper?: string; // Aide pour l'utilisateur
  isFixed?: boolean; // Indique si le paramètre a une valeur fixe
  fixedValue?: string; // Valeur fixe pour le paramètre si isFixed est true
  value?: string; // Valeur du paramètre si isFixed est false (au cours de la configuration)
  isOutputParam?: boolean; // Indique si le paramètre est une variable de sortie déclarée avant l'appel (ex: bs = 0)
  outputVarName?: string; // Nom de la variable de sortie (ex: "bs"), indépendant de fixedValue
  options?: Array<{
    label: string; // Libellé de l'option
    code: string; // Code de l'option
  }>; // Options pour le paramètre de type selection
}

// Types pour les variables SILAE
export type SilaeVariableType =
  | "date"
  | "int"
  | "string"
  | "double"
  | "boolean"
  | "selection"
  | "function"
  | "variable";

export type SilaeVariableCategory =
  | "bulletin"
  | "salary"
  | "employee"
  | "mtpart"
  | "functions"
  | "other";

export interface SilaeVariable {
  id: string;
  code: string; // Code de la variable (ex: "bul.mois", "sal.age")
  type: SilaeVariableType;
  definition: string; // Définition de la variable
  category: SilaeVariableCategory; // Catégorie de la variable (bulletin, salary, employee, mtpart, functions, other)
  example: string | null; // Exemple d'utilisation
  isActive: boolean; // Indique si la variable est active pour les utilisateurs
  isFrequentlyUsed: boolean; // Indique si la variable est fréquemment utilisée
  createdAt: string;
  updatedAt: string;
  displayName: string; // Nom user friendly affiché de la variable (ex: "mois du bulletin", "âge du salarié")
  categoryLabel: string; // Libellé de la catégorie de la variable (ex: "Bulletin", "Salaire", "Employé", "MTPART", "Fonctions", "Autre")
  // Champs additionnels pour les variables avancées
  label?: string; // Libellé alternatif pour l'affichage
  unit?: string; // Unité de la variable (ex: "jours", "€", "%")
  parameters?: Array<AdvancedVariableParams>;
  options?: Array<{
    label: string;
    code: string;
  }>;
}

export interface CreateSilaeVariableInput {
  code: string;
  type: SilaeVariableType;
  definition: string;
  category?: SilaeVariableCategory;
  example?: string;
  isActive?: boolean;
  isFrequentlyUsed?: boolean;
  label?: string;
  unit?: string;
  parameters?: Array<AdvancedVariableParams>;
  options?: Array<{
    label: string;
    code: string;
  }>;
}

export interface UpdateSilaeVariableInput {
  code?: string;
  type?: SilaeVariableType;
  definition?: string;
  category?: SilaeVariableCategory;
  example?: string;
  isActive?: boolean;
  isFrequentlyUsed?: boolean;
  label?: string;
  unit?: string;
  parameters?: Array<AdvancedVariableParams>;
  options?: Array<{
    label: string;
    code: string;
  }>;
}

export interface GetSilaeVariablesFilters {
  category?: SilaeVariableCategory;
  type?: SilaeVariableType;
  isActive?: boolean;
  isFrequentlyUsed?: boolean;
  search?: string;
}

// API Response types
export interface SilaeVariableApiResponse {
  success: boolean;
  data: SilaeVariable[];
  count?: number;
  message?: string;
}

export interface SingleSilaeVariableApiResponse {
  success: boolean;
  data: SilaeVariable;
  message?: string;
}

export interface VariableCountByCategory {
  [category: string]: number;
}

export interface VariableStatsApiResponse {
  success: boolean;
  data: VariableCountByCategory;
  message?: string; // Message d'erreur ou de succès
}
export interface SilaeFormData {
  structureType: StructureType; // Type de structure (simple, with_conditions, if_else)

  // Exclusions (partagées)
  hasExclusions: boolean; // Indique si on a des exclusions
  exclusions?: SilaeCondition[]; // Conditions d'exclusion
  exclusionsOperators: string[]; // Opérateurs logiques pour les exclusions (AND, OR)

  // Conditions principales (partagées)
  conditions?: SilaeCondition[]; // Conditions principales
  conditionsOperators: string[]; // Opérateurs logiques pour les conditions principales (AND, OR)

  // Branches
  ifBranch: SilaeBranchFormData; // Branche de la prime principale si les conditions est vraie
  elseBranch?: SilaeBranchFormData | undefined; // Branche de la prime alternative si les conditions est fausse

  // Commentaire libre attaché au diagramme (persisté, inclus dans l'export PDF)
  comment?: string;
}
export interface SilaeBranchFormData {
  // hasNestedCondition: boolean;
  bonuses: Array<SilaeBonusFormData>; // Primes de la branche
}

export interface SilaeBonusFormData {
  hasConditions: boolean; // Indique si la prime a des conditions
  conditions: SilaeCondition[]; // Conditions de la prime (ex: "bul.mois = 0", "sal.age > 30")
  conditionsOperators: string[]; // Opérateurs logiques (AND, OR)
  rubriqueNumber: string; // Numéro de la rubrique SILAE
  calculMethod: CalculMethod | undefined; // Méthode de calcul (1, 2, 3, 99)
  bases: Array<SilaeSourceFormData>; // Sources de base pour
  tauxs: Array<SilaeSourceFormData>; // Sources de taux pour le type calculMethod 1 ou 3
  libelle: string; // Libellé de la prime
  enableStockage: boolean; // Indique si la prime va stocker une variable
  stockageVariableName: string; // Nom de la variable à stocker (ex: "PRIME_PRECEDENTE", "PRIME_ACTUELLE")
  stockageVariableType: CounterAndStockageValueType; // le type de valeur à utiliser pour stocker une variable (bases, tauxs, bases_times_tauxs, custom)
  stockageCustomExpression: string; // Expression personnalisée pour stocker une variable
  enableMemos: boolean; // Indique si la prime a des mémos
  memoLines: string; // les lignes de mémo
  enableCounterFeeding: boolean; // Indique si la prime a un compteur à alimenter
  counterType: CounterType | ""; // le compteur à alimenter
  counterValueType: CounterAndStockageValueType; // le type de valeur à utiliser pour alimenter le compteur (bases, tauxs, bases_times_tauxs, custom)
  counterCustomExpression: string; // Expression personnalisée pour alimenter un compteur
  // Périodicité DSN + Bloc 52
  enableDsnPeriodicity: boolean; // Indique si la périodicité DSN est activée
  dsnDateDebut: string; // Date de début de la périodicité DSN (ex: "Date(1,1,bul.annee)")
  dsnDateFin: string; // Date de fin de la périodicité DSN (ex: "Date(31,12,bul.annee)")
  bloc52Code: string; // Code bloc 52 DSN (026, 027, 028, 029, 030 ou "custom")
  bloc52CustomCode: string; // Code bloc 52 personnalisé (3 chiffres) si bloc52Code === "custom"
}

export interface SilaeSourceFormData {
  id: string; // Identifiant unique de la source
  hasConditions: boolean; // Indique si la source a des conditions
  conditions: SilaeCondition[]; // Conditions pour le type qui ont des conditions
  conditionsOperators: string[]; // Opérateurs logiques (AND, OR)
  elseSource: SilaeSourceFormData | null; // Source alternative pour le type qui ont des conditions
  sourceType: SourceType; // Type de source (fixed, evp, salary_base, salary_gross, salary_net, stored_variable, cumul_bulletins, cumul_stock_var, advanced_variable, complex_expression, questionnaire_variable, manual_formula)
  amount: number; // Montant de la source pour le type fixed (ex: 100, 200, 300)
  evpTitle: string; // Titre de l'EVP
  evpDefaultValue: string; // Valeur par défaut de l'EVP
  salaryPercentage: string; // Pourcentage appliqué au salaires (base, brut, net)
  storedVariableName: string; // Nom de la variable stockée (ex: "PRIME_PRECEDENTE", "PRIME_ACTUELLE")
  advancedVariable: string; // Nom de la variable avancée (ex: "bul.mois", "sal.age")
  advancedVariablePercentage: string; // Pourcentage appliqué (pour types numériques)
  advancedVariableEnableAdditional: boolean; // Activer les opérations mathématiques additionnelles
  advancedVariableAdditionalOperator: "+" | "-" | "*" | "/"; // Opérateur mathématique additionnel
  advancedVariableAdditionalValue: string; // Valeur pour l'opération mathématique
  advancedVariableParameters: Array<AdvancedVariableParams>; // Paramètres pour les variables avancées type fonction
  advancedVariableMtpartValue?: string; // Valeur à remplacer dans mtpart.xxxx (ex: "ik" pour mtpart.ik) pour les variables avancées type fonction
  advancedVariablePostCallVarName: string; // Nom de la variable intermédiaire après l'appel (ex: "VAR_INTERMEDIAIRE")
  advancedVariablePostCallExpression: string; // Expression utilisant les variables de sortie (ex: "bs + ts - tp")
  complexExpression: Array<SilaeSourceFormData>; // Expressions complexes
  questionnaireVariableType?:
    | "double"
    | "int"
    | "boolean"
    | "date"
    | "text"
    | undefined; // "double", "int", "boolean", "date", "text"
  questionnaireVariableIndex?: string | undefined; // "1" à "10"
  questionnaireVariableLabel?: string | undefined; // Libellé personnalisé de la question
  questionnaireVariablePercentage: string; // Pourcentage appliqué (pour types numériques)
  questionnaireVariableValue: string; // Valeur de comparaison
  questionnaireVariableOperator: SilaeConditionOperator; // Opérateur de comparaison ("=", ">=", etc.)
  manualFormula: string; // Formule manuelle
  cumulPeriod: string; // Période prédéfinie (year_current, year_previous, etc.)
  cumulType: string; // Type de cumul (0-16)
  cumulCustomStartDate: string; // Date de début du cumul personnalisé
  cumulCustomEndDate: string; // Date de fin du cumul personnalisé
  cumulDivisor: string; // Diviseur pour le cumul
  cumulStockVarName: string; // Nom de la variable stockée à cumuler
  cumulStockVarPeriod: string; // Période prédéfinie (year_current, year_previous, etc.)
  cumulStockVarDateDebut: string; // Date de début du cumul
  cumulStockVarDateFin: string; // Date de fin du cumul
  cumulStockVarPercentage: string; // Pourcentage appliqué au cumul
  cumulStockVarEnableAdditional: boolean; // Activer les opérations mathématiques additionnelles
  cumulStockVarAdditionalOperator: "+" | "-" | "*" | "/" | undefined; // Opérateur mathématique (+, -, *, /)
  cumulStockVarAdditionalValue: string; // Valeur pour l'opération mathématique
  enableEvpForce: boolean; // Activer la force de l'EVP
  evpForceTitle: string; // Titre de la force de l'EVP
  variableName?: string; // Nom de la variable pour les expressions complexes (ex: "bul.mois", "sal.age")
  mathOperator?: "+" | "-" | "*" | "/"; // Opérateur mathématique pour les expressions complexes
}

// Structure d'une condition SILAE
export interface SilaeCondition {
  id: string;
  type: SilaeConditionType;

  silaeVariable?: string; // Code de la variable (ex: "bul.mois", "sal.age")
  conditionOperator?: SilaeConditionOperator; // "=", ">=", "<=", "<>", ">", "<"
  conditionValue?: string; // Valeur à comparer
  conditionPercentage?: string; // Pourcentage pour les variables int/double (défaut: 100%) ou function (défaut: 100%)
  mtpartValue?: string; // Valeur à remplacer dans mtpart.xxxx (ex: "ik" pour mtpart.ik)
  functionParameters?: Array<AdvancedVariableParams>; // Paramètres pour les fonctions SILAE

  // Condition CumulStockVar
  cumulStockVarName?: string; // Nom de la variable stockée à cumuler
  cumulStockVarPeriod?: string; // Période prédéfinie (year_current, year_previous, etc.)
  cumulStockVarDateDebut?: string; // Date de début du cumul
  cumulStockVarDateFin?: string; // Date de fin du cumul
  cumulStockVarPercentage?: string; // Pourcentage appliqué au cumul
  cumulStockVarEnableAdditional?: boolean; // Activer les opérations mathématiques additionnelles
  cumulStockVarAdditionalOperator?: SilaeConditionOperator; // Opérateur mathématique (+, -, *, /)
  cumulStockVarAdditionalValue?: string; // Valeur pour l'opération mathématique

  // Condition CumulBulletins
  cumulBulletinsDateDebut?: string; // Date de début du cumul
  cumulBulletinsDateFin?: string; // Date de fin du cumul
  cumulBulletinsType?: string; // Type de cumul (0-16)
  cumulBulletinsDivisor?: string; // Diviseur pour le cumul
  cumulBulletinsPeriod?: string; // Période prédéfinie (year_current, year_previous, etc.)

  // Condition Variable Questionnaire Salarié
  questionnaireVariableType?: "double" | "int" | "boolean" | "date" | "text"; // "double", "int", "boolean", "date", "text"
  questionnaireVariableIndex?: string; // "1" à "10"
  questionnaireVariableLabel?: string; // Libellé personnalisé de la question
  questionnaireVariablePercentage?: string; // Pourcentage appliqué (pour types numériques)
  questionnaireVariableValue?: string; // Valeur de comparaison
  questionnaireVariableOperator?: SilaeConditionOperator; // Opérateur de comparaison ("=", ">=", etc.)
}

// Structure des codes de rubriques SILAE
export interface RubriqueCode {
  code: string;
  label: string;
  description: string;
  category: string;
  properties: {
    imposable: boolean;
    soumis_cotisations: boolean;
    base_cp: boolean;
  };
}

// Props pour les composants de formulaire réutilisables
export interface RubriqueSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  onLabelAutoFill?: (label: string) => void;
  isManualEntry: boolean;
  onManualEntryChange: (isManual: boolean) => void;
  placeholder?: string;
  error?: string;
  touched?: boolean;
  isElse?: boolean;
  onBlur?: (fieldName: string) => void;
}

export interface BaseTypeSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  error?: string;
  touched?: boolean;
  disableComplexExpression?: boolean;
}

/**
 * Types de cumul disponibles pour les bulletins
 * Ces codes sont utilisés dans les fonctions cumulbulletins()
 */
export const cumulTypes: Record<string, string> = {
  "0": "Cumul des salaires de base",
  "1": "Cumul des bruts",
  "2": "Somme des BrutAL",
  "3": "Cumul NBHMN + NBHMM",
  "4": "Brut hors congés payés",
  "5": "Cumuler les PSS",
  "6": "Nombre de mois avec bulletins",
  "7": "Somme des heures contrats",
  "10": "Cumul heures contrats (étendu)",
  "11": "Réintégration Sociale",
  "12": "Sigma BUL.SALAIREDEBASEHEURES",
  "13": "Base CSG",
  "14": "Base CSG non abattue",
  "15": "Heures normales contractuelles",
  "16": "Réintégration Sociale Agirc Arrco",
};
